import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaImages, FaUpload, FaDownload, FaTrash } from 'react-icons/fa';
import { getMyPhotos, getImageUrl, getFlaskUploadUrl, downloadPhoto, removeMyPhoto } from '../services/api';
import { useAuth } from '../context/AuthContext';

const getPhotoDisplayName = (photo) => {
  const name = photo?.name || photo?.title || photo?.filename || `Photo ${photo?.id || ''}`;
  return name.split('/').pop() || name;
};

function MyPhotos() {
  const { user } = useAuth();
  const [photoGroups, setPhotoGroups] = useState([]);
  const [totals, setTotals] = useState({ photos: 0, events: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [deletingPhotoId, setDeletingPhotoId] = useState(null);

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    try {
      const response = await getMyPhotos();
      setPhotoGroups(response.data);
      setTotals({ photos: response.total_photos, events: response.total_events });
    } catch (err) {
      setError(err.message || 'Impossible de charger les photos');
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePhoto = async (photoId) => {
    if (!window.confirm('Retirer cette photo de vos photos découvertes ?')) return;

    setDeletingPhotoId(photoId);
    setMessage({ type: '', text: '' });

    try {
      await removeMyPhoto(photoId);

      setPhotoGroups(prev => {
        const nextGroups = prev
          .map(group => ({
            ...group,
            photos: group.photos.filter(photo => photo.id !== photoId),
          }))
          .filter(group => group.photos.length > 0);

        setTotals({
          photos: nextGroups.reduce((sum, group) => sum + group.photos.length, 0),
          events: nextGroups.length,
        });

        return nextGroups;
      });

      setMessage({ type: 'success', text: 'Photo retirée de vos photos découvertes.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Impossible de retirer la photo' });
    } finally {
      setDeletingPhotoId(null);
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '4rem' }}>
        <div className="spinner"></div>
        <p>Chargement de vos photos...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container" style={{ padding: '4rem 2rem' }}>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (!user?.profile_photo) {
    return (
      <div className="container" style={{ padding: '4rem 2rem', textAlign: 'center' }}>
        <FaUpload style={{ fontSize: '4rem', color: 'var(--primary-orange)', marginBottom: '1rem' }} />
        <h2 style={{ color: 'var(--dark-gray)', marginBottom: '1rem' }}>
          Aucune photo de profil pour le moment
        </h2>
        <p style={{ color: 'var(--gray)', marginBottom: '2rem', fontSize: '1.1rem' }}>
          Importez votre photo de profil pour commencer à découvrir les photos où vous apparaissez !
        </p>
        <Link to="/profile-upload" className="btn-primary">
          Importer une photo de profil
        </Link>
      </div>
    );
  }

  if (photoGroups.length === 0) {
    return (
      <div className="container" style={{ padding: '4rem 2rem', textAlign: 'center' }}>
        <FaImages style={{ fontSize: '4rem', color: 'var(--gray)', marginBottom: '1rem' }} />
        <h2 style={{ color: 'var(--dark-gray)', marginBottom: '1rem' }}>
          Aucune correspondance trouvée pour le moment
        </h2>
        <p style={{ color: 'var(--gray)', fontSize: '1.1rem' }}>
          Nous n'avons pas encore trouvé de photos correspondant à votre profil.
          Revenez plus tard lorsque les photographes auront importé d'autres photos d'événements !
        </p>
      </div>
    );
  }

  return (
    <div className="photo-gallery">
      <div className="container">
        <h1 style={{ color: 'var(--primary-orange)', marginBottom: '1rem' }}>
          Vos photos découvertes
        </h1>
        <p style={{ color: 'var(--gray)', marginBottom: '2rem', fontSize: '1.1rem' }}>
          {totals.photos} photos trouvées dans {totals.events} événements
        </p>
        {message.text && (
          <div className={message.type === 'error' ? 'error-message' : 'success-message'} style={{ marginBottom: '1.5rem' }}>
            {message.text}
          </div>
        )}

        {photoGroups.map((group) => (
          <div key={group.event_id} className="event-section">
            <div className="event-header">
              <h3>{group.event_name}</h3>
              <div className="event-meta">
                <span className="category-badge">{group.event_category}</span>
                {group.event_date && (
                  <span>📅 {new Date(group.event_date).toLocaleDateString()}</span>
                )}
                <span>📸 {group.photos.length} photos</span>
              </div>
            </div>

            <div className="photos-grid">
              {group.photos.map((photo) => (
                <div key={photo.id} className="photo-card">
                  <img
                    src={getImageUrl(photo.filename)}
                    alt={`Correspondance depuis ${group.event_name}`}
                    onError={(e) => {
                      if (e.currentTarget.dataset.fallbackUsed) {
                        e.currentTarget.src = 'https://placehold.co/300x200/png';
                        return;
                      }

                      e.currentTarget.dataset.fallbackUsed = 'true';
                      e.currentTarget.src = getFlaskUploadUrl(photo.filename);
                    }}
                  />
                  <div className="photo-info">
                    <div
                      title={getPhotoDisplayName(photo)}
                      style={{
                        color: 'var(--dark-gray)',
                        fontSize: '0.95rem',
                        fontWeight: '700',
                        marginBottom: '0.75rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {getPhotoDisplayName(photo)}
                    </div>
                    <div className="confidence-score">
                      {photo.confidence_score}% de correspondance
                    </div>
                    <div className="confidence-bar">
                      <div
                        className="confidence-fill"
                        style={{ width: `${photo.confidence_score}%` }}
                      ></div>
                    </div>
                    <p style={{ fontSize: '0.9rem', color: 'var(--gray)', marginTop: '0.5rem' }}>
                      Importée le : {new Date(photo.uploaded_at).toLocaleDateString()}
                    </p>
                    <button
                      onClick={async () => {
                        try {
                          const blob = await downloadPhoto(photo.id);
                          const url = window.URL.createObjectURL(new Blob([blob]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = getPhotoDisplayName(photo);
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          window.URL.revokeObjectURL(url);
                        } catch (err) {
                          console.error('Download failed:', err);
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        marginTop: '0.5rem',
                        background: 'var(--primary-green)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontWeight: '600'
                      }}
                    >
                      <FaDownload /> Télécharger
                    </button>
                    <button
                      onClick={() => handleRemovePhoto(photo.id)}
                      disabled={deletingPhotoId === photo.id}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        marginTop: '0.5rem',
                        background: deletingPhotoId === photo.id ? 'var(--gray)' : '#f44336',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: deletingPhotoId === photo.id ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.4rem'
                      }}
                    >
                      <FaTrash /> {deletingPhotoId === photo.id ? 'Retrait...' : 'Retirer'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default MyPhotos;
